import React, { useEffect, useState } from 'react'
import Parse from 'parse'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { setAuthenticated, setUserData, setPlayerName } from '../../stores/UserStore'
import store from '../../stores'
import { ServiceFactory } from '../factories/ServiceFactory'
import AuthenticationDialog from '../../components/AuthenticationDialog'
import { RootState } from '../../stores'
import authService from '../../services/authService'
import type { UserData, UserRole } from '../../types/user'
import { readParseGoogleLinked, userProfileToFullUserData } from '../../account'
import { normalizeRoutePath } from '../../utils/routeUtils'
import { getDisplayName } from '../../utils/personName'

/**
 * Controller component for Authentication features
 * Manages the authentication dialog and handles its visibility based on game loading state
 * Ensures profile data is properly loaded and cached after authentication
 */
const AuthenticationController: React.FC = (): JSX.Element | null => {
  const dispatch = useAppDispatch()
  const loadingGame = useAppSelector((state: RootState) => state.game.loadingGame)
  const [isInitializing, setIsInitializing] = useState(true)
  const [pendingVerifyRedirect, setPendingVerifyRedirect] = useState(false)

  const authServiceInstance = ServiceFactory.getInstance().getAuthService()

  useEffect(() => {
    const checkSession = async () => {
      try {
        const user = await authServiceInstance.checkSession()
        
        if (user) {
          const parseEmailVerified = (() => {
            try {
              const pu = Parse.User.current()
              const v = pu?.get('emailVerified')
              if (v === true) return true
              if (v === false) return false
              return undefined
            } catch {
              return undefined
            }
          })()

          const path =
            typeof window !== 'undefined' ? normalizeRoutePath(window.location.pathname) : '/'
          if (parseEmailVerified === false && path !== '/verify-email') {
            setPendingVerifyRedirect(true)
            window.location.replace('/verify-email')
            return
          }

          // CRITICAL FIX: Load profile data immediately after session check
          try {
            const profile = await authService.getCurrentProfile()
            
            if (profile) {
              const userData = userProfileToFullUserData(
                {
                  id: user.id,
                  email: user.email,
                  username: user.username,
                  role: (profile.role as UserRole) || 'user',
                  createdAt: user.createdAt || new Date().toISOString(),
                  emailVerified: parseEmailVerified,
                },
                profile,
                readParseGoogleLinked()
              )

              dispatch(setUserData(userData))
              
              // Update player name mapping for immediate UI updates (only if in a room session)
              const displayName = getDisplayName(userData)
              const sessionId = store.getState().user.sessionId
              if (sessionId) {
                dispatch(setPlayerName({ id: sessionId, name: displayName }))
              }
              
              dispatch(setAuthenticated(true))
            } else {
              // Fallback to basic user data if no profile
              const userData: UserData = {
                id: user.id,
                email: user.email,
                username: user.username,
                role: 'user' as UserRole,
                createdAt: user.createdAt || new Date().toISOString(),
                emailVerified: parseEmailVerified,
                firstName: '',
                lastName: '',
                title: '',
                contact: '',
                bio: '',
                avatar: 'adam',
                gameCharacter: 'adam',
                customAvatarUrl: '',
                profileCompleted: false,
                googleLinked: readParseGoogleLinked(),
              }
              
              dispatch(setUserData(userData))
              const fallbackSessionId = store.getState().user.sessionId
              if (fallbackSessionId) {
                dispatch(setPlayerName({ id: fallbackSessionId, name: userData.username || 'User' }))
              }
              dispatch(setAuthenticated(true))
            }
          } catch (profileError) {
            // Fallback to basic user data
            const userData: UserData = {
              id: user.id,
              email: user.email,
              username: user.username,
              role: 'user' as UserRole,
              createdAt: user.createdAt || new Date().toISOString(),
              emailVerified: parseEmailVerified,
              firstName: '',
              lastName: '',
              title: '',
              contact: '',
              bio: '',
              avatar: 'adam',
              gameCharacter: 'adam',
              customAvatarUrl: '',
              profileCompleted: false,
              googleLinked: readParseGoogleLinked(),
            }
            
            dispatch(setUserData(userData))
            const catchSessionId = store.getState().user.sessionId
            if (catchSessionId) {
              dispatch(setPlayerName({ id: catchSessionId, name: userData.username || 'User' }))
            }
            dispatch(setAuthenticated(true))
          }
        }
      } catch (error) {
        // Silent error handling for production
      } finally {
        setIsInitializing(false)
      }
    }

    checkSession()
  }, [dispatch, authServiceInstance])

  // Don't render dialog when game is loading, initializing, or navigating to email verification
  if (loadingGame || isInitializing || pendingVerifyRedirect) return null

  return <AuthenticationDialog />
}

export default AuthenticationController 